export function lineReserved(qty: number, scannedOrShipped: number): number {
  return Math.max(qty - scannedOrShipped, 0);
}

export function availableQty(inStock: number, reserved: number): number {
  return inStock - reserved;
}

export interface Shortage {
  skuId: string;
  requested: number;
  available: number;
}

export function findShortages(requested: { skuId: string; qty: number }[], availableBySku: Record<string, number>): Shortage[] {
  const totals = new Map<string, number>();
  for (const line of requested) totals.set(line.skuId, (totals.get(line.skuId) ?? 0) + line.qty);
  const out: Shortage[] = [];
  for (const [skuId, qty] of [...totals.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const available = availableBySku[skuId] ?? 0;
    if (qty > available) out.push({ skuId, requested: qty, available });
  }
  return out;
}

/** A SKU at a site with fewer than this many available, and more than none, is low. */
export const LOW_BELOW = 10;

/**
 * How one SKU stands at one site, from what is available there: the one rule for "low" and "out"
 * that Office → Stock, the dashboard and the Wall all use.
 *
 *  - `short`: below zero, more promised to orders than is on the shelf;
 *  - `out`: none available;
 *  - `low`: 1 to 9 available (`LOW_BELOW`);
 *  - `ok`: 10 or more.
 *
 * It judges one site at a time, never a total across sites, and callers judge live sites only: a
 * site that is not live yet is empty by design. A (site, SKU) under an open count is not judged at
 * all: its figure is the count's answer, and a level would give it away (the Wall's blind rule).
 */
export type StockLevel = 'short' | 'out' | 'low' | 'ok';

export function stockLevel(available: number): StockLevel {
  if (available < 0) return 'short';
  if (available === 0) return 'out';
  if (available < LOW_BELOW) return 'low';
  return 'ok';
}
